import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import NavBar from "./NavBar";
import BackgroundImage from "./../images/contactus_background.jpg";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { axiosReqConfig } from "../functions/axiosRequest";
import { API_IP } from "../global_variables";
import {
  subscribeTopic,
  getSubChannelTopic,
  MessageContentType,
  MessageRepository,
} from "@amityco/ts-sdk";
import { createTextMessage } from "./Amity/AmityProvider.ts";
import { sendChatPushNotification } from "./Amity/chatPushHelper";
import { useChatNotification } from "./Amity/useChatNotification";
import StarRating from "./UserPatientScreen/StarRating";
import AmityErrorBoundary from "./Amity/AmityErrorBoundary";
import { formatDistanceToNowStrict } from "date-fns";
import axios from "axios";
import "./Messages.css"

const MessagingPage = () => {
  const [list, setList] = useState([]);
  const [chatBoxName, setChatBoxName] = useState("");
  const [subChannelId, setSubChannelId] = useState(null);
  const [creator, setCreator] = useState("");
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [query, setQuery] = useState("");
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerUserId, setProviderUserId] = useState(""); // Amity ID of the other participant e.g. "P5"
  const [previews, setPreviews] = useState({});
  const [unreadChannels, setUnreadChannels] = useState({});
  const messageBottomRef = useRef(null);
  const selectedChannelIdRef = useRef(null);
  const [isChatLoading, setIsChatLoading] = useState(false);

  const username = localStorage.getItem("username");
  const disposers = [];
  const subscribeSubChannel = (subChannel) => {
    disposers.push(subscribeTopic(getSubChannelTopic(subChannel)));
  };

  // ── In-browser + OS notifications for incoming messages ──────────────────
  const { requestPermission } = useChatNotification({
    channels: list,
    currentCreatorId: creator,
    activeChannelId: selectedChannelId,
  });
  const handleSearchChange = (e) => {
    setQuery(e.target.value);
  };

  const handleChangeMessage = (e) => {
    setNewMessage(e.target.value);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!subChannelId) return;
    if (!newMessage) return;

    const textToSend = newMessage.trim();
    if (!textToSend) return;

    const senderName =
      selectedProvider?.client_name_array?.[0] || username;
    await createTextMessage(subChannelId, textToSend, senderName);
    setNewMessage("");
    scrollToTarget();

    // Fire-and-forget: push FCM notification to the Provider
    if (providerUserId) {
      sendChatPushNotification({
        recipientUserId: providerUserId,
        senderName: senderName || username || "Client",
        messagePreview: textToSend,
        channelCode: selectedChannelId || subChannelId,
      });
    }
  };
  const getChannelMessages = useCallback(() => {
    const unsubscribe = MessageRepository.getMessages(
      { subChannelId, limit: 10000 },
      ({ data: messages, onNextPage, hasNextPage, loading, error }) => {
        messages.sort((a, b) => a.channelSegment - b.channelSegment);
        setMessages(messages);
      }
    );
    disposers.push(unsubscribe);
  }, [subChannelId, disposers]);

  useEffect(() => {
    selectedChannelIdRef.current = selectedChannelId;
  }, [selectedChannelId]);

  // channelKey  = item.channel_code  (used as the React state key)
  // subChannelId = item.sub_channel_id || item.channel_code  (used for Amity API)
  // Community channels created after provider-accept have different channelId vs subChannelId.
  const watchLatestMessage = useCallback((channelKey, subChannelId) => {
    if (!subChannelId) return () => {};

    const unsub = MessageRepository.getMessages(
      { subChannelId, limit: 20, isDeleted: false },
      ({ data }) => {
        if (!data || !data.length) {
          setUnreadChannels((prev) => ({ ...prev, [channelKey]: false }));
          return;
        }

        const latest = data.reduce((a, b) => {
          if (!a) return b;
          const segA = a.channelSegment ?? 0;
          const segB = b.channelSegment ?? 0;
          return segB > segA ? b : a;
        }, null);

        setPreviews((prev) => ({
          ...prev,
          [channelKey]: {
            text:
              latest?.data?.text ??
              (latest?.dataType === "image" ? "📷 Photo" : "New message"),
            createdAt: latest?.createdAt,
            creatorId: latest?.creatorId,
          },
        }));

        const isUnread =
          latest?.creatorId &&
          latest.creatorId !== creator &&
          channelKey !== selectedChannelIdRef.current;
        setUnreadChannels((prev) => ({ ...prev, [channelKey]: Boolean(isUnread) }));
      }
    );

    return unsub;
  }, [creator]);

  const onClickProvider = (item) => {
    setSelectedProvider(item);
    setSelectedChannelId(item.channel_code);
    setSubChannelId(item.sub_channel_id || item.channel_code);
    setUnreadChannels((prev) => ({ ...prev, [item.channel_code]: false }));

    // Derive the Provider's Amity user ID for sending push notifications
    // provider_id_array comes from the API; fall back to empty string
    const pid = item.provider_id_array?.[0];
    setProviderUserId(pid ? "P" + pid : "");
  };

  const getChatList = async () => {
    try {
      const authToken = localStorage.getItem("token");
      const apiUrl = `${API_IP}/api/amity/channels`;
      const username = localStorage.getItem("username");
      axios
        .get(apiUrl, {
          params: { username: username },
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        })
        .then((response) => {
          setCreator("A" + response.data.userId);
          setList(response.data.chatList);
          console.log(response.data.chatList);
        })
        .catch((error) => {
          console.error(
            "Error fetching data from the API (getChannels):",
            error
          );
        });
    } catch (error) {
      console.log(error);
    }
  };
  const filteredAndSortedList = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = normalizedQuery
      ? list.filter((item) => {
          const providerName = (item.provider_array?.[0] || "").toLowerCase();
          const providerTitle = (item.provider_title?.[0] || "").toLowerCase();
          return (
            providerName.includes(normalizedQuery) ||
            providerTitle.includes(normalizedQuery)
          );
        })
      : list;

    return [...filtered].sort((a, b) => {
      const aPreview = previews[a.channel_code];
      const bPreview = previews[b.channel_code];
      const aTime = new Date(aPreview?.createdAt || a.updatedAt || a.createdAt || 0).getTime();
      const bTime = new Date(bPreview?.createdAt || b.updatedAt || b.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }, [list, previews, query]);

  const ChatListItem = ({ index, item, isSelected }) => {
    const scid = item.channel_code;
    const preview = previews[scid];
    const isUnread = unreadChannels[scid] && !isSelected;
    const previewCreatedAt = preview?.createdAt || item.updatedAt || item.createdAt;
    return (
      <button
        disabled={isSelected}
        onClick={() => onClickProvider(item)}
        className={`MSG-list-item ${isSelected ? "MSG-item-active" : ""} ${
          isUnread ? "MSG-item-unread" : ""
        }`}
      >
        <div className="MSG-item-header">
          <img
            src={item.provider_img[0] ?? require("../images/avatar_placeholder_0.png")}
            alt="Provider Avatar"
            className="MSG-item-avatar"
          />
          <div className="MSG-item-name-wrapper">
            <div>{item.provider_array[0]}</div>
            <div className="MSG-item-title">{item.provider_title[0] ?? "Care Provider"}</div>
          </div>
          {isUnread ? <span className="MSG-unread-dot" /> : null}
        </div>
        <div className="MSG-item-preview">
          <div>{preview?.text ?? "Start a conversation"}</div>
          <div>
            {previewCreatedAt
              ? formatDistanceToNowStrict(new Date(previewCreatedAt), { addSuffix: true })
              : ""}
          </div>
        </div>
      </button>
    );
  };

  const MessageBubble = ({ message }) => {
    const messageDate = new Date(message.createdAt);
    const messageDateFormat = messageDate.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    const isSent = message.creatorId === creator;
    return (
      <div className={`MSG-bubble-wrapper ${isSent ? "MSG-sender" : "MSG-receiver"}`}>
        <div className={`MSG-bubble ${isSent ? "MSG-bubble-sent" : "MSG-bubble-received"}`}>
          {!isSent && message.metadata?.data?.sender && (
            <div className="MSG-bubble-sender-name">{message.metadata.data.sender}</div>
          )}
          <div>{message.data?.text}</div>
          <div className="MSG-bubble-time">{messageDateFormat}</div>
        </div>
      </div>
    );
  };

  const scrollToTarget = () => {
    if (messageBottomRef.current) {
      messageBottomRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      getChatList();
      // Request browser notification permission on first load
      requestPermission();
    }
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const disposers = [];
    list.forEach((item) => {
      const channelKey = item.channel_code;
      const scid = item.sub_channel_id || item.channel_code;
      const d = watchLatestMessage(channelKey, scid);
      if (d) disposers.push(d);
    });

    return () => disposers.forEach((fn) => fn && fn());
  }, [list, watchLatestMessage]);

  useEffect(() => {
    if (!selectedChannelId) return;
    const matched = list.find((item) => item.channel_code === selectedChannelId);
    if (matched) {
      setSelectedProvider(matched);
    }
  }, [list, selectedChannelId]);

  useEffect(() => {
    if (!subChannelId) return;

    setIsChatLoading(true);
    const unsubscribe = MessageRepository.getMessages(
      { subChannelId, limit: 50 },
      ({ data, onNextPage, hasNextPage, loading, error }) => {
        const sorted = [...data].sort(
          (a, b) => a.channelSegment - b.channelSegment
        );
        setMessages(sorted);
        setUnreadChannels((prev) => ({ ...prev, [subChannelId]: false }));
        setIsChatLoading(loading);
      }
    );

    return () => unsubscribe?.();
  }, [subChannelId]);
  return (
    <AmityErrorBoundary>
      <div className="MSG-container screen-container">
        {/* Page Header */}
        <header className="screen-header">
          <h1 className="screen-title">Messages</h1>
          <p className="screen-subtitle">
            Connect with your care providers and manage your conversations.
          </p>
        </header>

        <div className="MSG-layout">
          {/* Sidebar */}
          <div className="MSG-sidebar">
            <div className="MSG-search-wrapper">
              <img src={require("../images/search-icon.png")} className="MSG-search-icon" alt="search" />
              <input
                type="text"
                value={query}
                onChange={handleSearchChange}
                placeholder="Search providers..."
                className="MSG-search-input"
              />
            </div>
            <div className="MSG-sidebar-header">
              {filteredAndSortedList.length} Conversation{filteredAndSortedList.length !== 1 ? "s" : ""}
            </div>
            <div className="MSG-list-body">
              {list.length !== 0 ? (
                filteredAndSortedList.map((target, index) => (
                  <ChatListItem
                    key={target.channel_code}
                    index={index}
                    isSelected={selectedChannelId === target.channel_code}
                    item={target}
                  />
                ))
              ) : (
                <div className="MSG-empty-state">
                  <img src={require("../images/no-message-pic.png")} className="MSG-empty-image" alt="No messages" />
                  <span className="MSG-empty-text">No conversations yet</span>
                </div>
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="MSG-chat-area">
            <div className="MSG-chat-header">
              {selectedProvider ? (
                <div className="MSG-chat-header-info">
                  <img
                    src={selectedProvider.provider_img?.[0] ?? require("../images/avatar_placeholder_0.png")}
                    alt="Provider"
                    className="MSG-chat-avatar"
                  />
                  <div>
                    <div className="MSG-chat-name">{selectedProvider.provider_array[0]}</div>
                    <div className="MSG-chat-title">{selectedProvider.provider_title[0] ?? "Care Provider"}</div>
                  </div>
                </div>
              ) : (
                <div style={{ color: "#94a3b8", fontWeight: 500, fontSize: 15 }}>
                  Select a conversation to start messaging
                </div>
              )}
            </div>

            <div className="MSG-chat-messages" id="chatBox">
              {selectedProvider == null ? (
                <div className="MSG-empty-state">
                  <span style={{ fontSize: 48 }}>💬</span>
                  <span className="MSG-empty-text">No chat selected</span>
                  <span className="MSG-empty-subtext">Choose a provider from the sidebar to view your conversation</span>
                </div>
              ) : isChatLoading ? (
                <div className="MSG-empty-state">
                  <img src={require("../images/loading_icon.gif")} alt="Loading" style={{ width: "36px", marginBottom: "10px" }} />
                  <span className="MSG-empty-text">Loading messages...</span>
                </div>
              ) : (
                <>
                  {messages.map((m) => (
                    <MessageBubble key={m.messageId} message={m} />
                  ))}
                  <div ref={messageBottomRef} />
                </>
              )}
            </div>

            <form className="MSG-chat-input-form" onSubmit={(e) => handleSendMessage(e)}>
              <div className="MSG-input-wrapper">
                <input
                  type="text"
                  value={newMessage}
                  placeholder="Type a message..."
                  onChange={handleChangeMessage}
                  className="MSG-input-field"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) handleSendMessage(e);
                  }}
                />
                <button type="submit" className="MSG-send-btn">
                  <img src={require("../images/send-icon.png")} alt="Send" />
                </button>
              </div>
            </form>
          </div>

          {/* Detail Panel */}
          <div className="MSG-detail-area">
            {selectedProvider ? (
              <>
                <div className="MSG-detail-avatar-wrapper">
                  <img
                    src={selectedProvider.provider_img?.[0] ?? require("../images/avatar_placeholder_0.png")}
                    alt="Provider"
                    className="MSG-detail-avatar"
                  />
                </div>
                <div className="MSG-detail-name">{selectedProvider.provider_array[0]}</div>
                <div className="MSG-detail-role">{selectedProvider.provider_title[0] ?? "Care Provider"}</div>
                <StarRating rating={4} maxStars={5} />
                <div className="MSG-detail-divider" />
                <div className="MSG-detail-info">
                  <div className="MSG-detail-info-row">
                    <span className="MSG-detail-info-label">📞 Phone</span>
                    <span className="MSG-detail-info-value">{selectedProvider.provider_phone?.[0] || "—"}</span>
                  </div>
                  <div className="MSG-detail-info-row">
                    <span className="MSG-detail-info-label">✉️ Email</span>
                    <span className="MSG-detail-info-value">{selectedProvider.provider_email?.[0] || "—"}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="MSG-empty-state">
                <span style={{ fontSize: 40 }}>👤</span>
                <span className="MSG-empty-subtext">Provider details will appear here</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </AmityErrorBoundary>
  );
};

export default MessagingPage;
